// pages/fit/FitOrderDetails.js - VERSION CORRIGÉE
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import VeryfitLoader from "../../components/VeryfitLoader";

const FitOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});
  const db = getFirestore();
  const formRefs = useRef([]);

  // 🎯 FONCTION POUR IDENTIFIER LE RÔLE UTILISATEUR
  const getUserRole = (order) => {
    if (!order) return null;
    
    const destinataireType = order.destinataire_type?.toLowerCase();
    
    if (destinataireType === "carrossier") return "CARROSSIER";
    if (destinataireType === "revendeur" || destinataireType === "controleur") return "REVENDEUR";
    if (destinataireType === "utilisateur") return "UTILISATEUR";
    
    const email = order.revendeurEmail?.toLowerCase() || "";
    if (email.includes("carrossier")) return "CARROSSIER";
    if (email.includes("revendeur") || email.includes("controleur")) return "REVENDEUR";
    
    return "UTILISATEUR";
  };

  // 🎯 FONCTION POUR OBTENIR LES DOCUMENTS AUTORISÉS PAR RÔLE
  const getAuthorizedDocuments = (role) => {
    switch(role) {
      case "CARROSSIER":
        return {
          declarationMontage: true,
          miseEnService: true, 
          declarationCE: true,
          noticeInstruction: true,
          controlePeriodique: false
        };
        
      case "REVENDEUR":
        return {
          declarationMontage: false,
          miseEnService: false,
          declarationCE: false, 
          noticeInstruction: true,
          controlePeriodique: true
        };
        
      case "UTILISATEUR":
        return {
          declarationMontage: false,
          miseEnService: false,
          declarationCE: true,
          noticeInstruction: true,
          controlePeriodique: true
        };
        
      default:
        return {
          declarationMontage: false,
          miseEnService: false,
          declarationCE: false,
          noticeInstruction: false,
          controlePeriodique: false
        };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!orderId) {
        console.error("❌ Aucun orderId fourni");
        setLoading(false);
        return;
      }

      try {
        console.log("🔍 Récupération du dossier:", orderId);
        const ref = doc(db, "dossiers", orderId);
        const snap = await getDoc(ref);
        
        if (snap.exists()) {
          const data = snap.data();
          console.log("✅ Dossier trouvé:", data);
          
          const processedData = {
            id: snap.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          };
          setOrder(processedData);

          const userRole = getUserRole(processedData);
          const authorizedDocs = getAuthorizedDocuments(userRole);
          console.log("🎯 Rôle détecté:", userRole);
          console.log("📋 Documents autorisés:", authorizedDocs);
          
        } else {
          console.error("❌ Dossier introuvable:", orderId);
        }
      } catch (e) {
        console.error("❌ Erreur Firestore:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, db]);

  // 🔥 FONCTION CORRIGÉE : Génère les mêmes IDs que le carrossier
  const generateDeclarationId = (productName, productId, index = 0) => {
    if (!productName && !productId) return null;
    
    // Utilise le même pattern que le carrossier
    const baseId = productId || productName;
    return index > 0 ? `${baseId}_${index}` : baseId;
  };

  // 🔥 FONCTION CORRIGÉE : Lit les données avec les bonnes clés
  const getDeclarationMontageStatus = (product, productIndex = 0) => {
    if (!order || !product) {
      console.log("❌ getDeclarationMontageStatus: Pas d'order ou de product");
      return false;
    }
    
    // Génère le même declarationId que le carrossier
    const declarationId = generateDeclarationId(product.name, product.productId, productIndex);
    if (!declarationId) {
      console.log("❌ Impossible de générer declarationId pour:", product);
      return false;
    }
    
    // 🔥 CORRECTION : Chercher d'abord avec _unit_1, puis sans
    const possibleKeys = [
      `${declarationId}_unit_1_montage_complete`,  // Format avec _unit_1
      `${declarationId}_montage_complete`,         // Format sans _unit
      `${product.productId}_unit_1_montage_complete`, // Fallback avec productId
      `${product.productId}_montage_complete`      // Fallback simple
    ];
    
    let isComplete = false;
    let foundKey = null;
    
    for (const key of possibleKeys) {
      if (order[key] === true) {
        isComplete = true;
        foundKey = key;
        break;
      }
    }
    
    console.log(`🔍 Vérification déclaration montage:`, {
      productName: product.name,
      productId: product.productId,
      declarationId,
      possibleKeys,
      foundKey,
      isComplete,
      orderKeys: Object.keys(order).filter(k => k.includes('montage'))
    });
    
    return isComplete;
  };

  // 🔥 FONCTION CORRIGÉE : Lit les données de déclaration
  const getDeclarationMontageData = (product, productIndex = 0) => {
    if (!order || !product) return null;
    
    const declarationId = generateDeclarationId(product.name, product.productId, productIndex);
    if (!declarationId) return null;
    
    // 🔥 CORRECTION : Chercher les données avec les bons préfixes
    const possibleDataKeys = [
      `declarationMontage_${declarationId}_unit_1`,  // Format avec _unit_1
      `declarationMontage_${declarationId}`,         // Format sans _unit
      `declarationMontage_${product.productId}_unit_1`, // Fallback avec productId
      `declarationMontage_${product.productId}`      // Fallback simple
    ];
    
    let data = null;
    let foundDataKey = null;
    
    for (const key of possibleDataKeys) {
      if (order[key]) {
        data = order[key];
        foundDataKey = key;
        break;
      }
    }
    
    console.log(`🔍 Recherche données déclaration:`, {
      productName: product.name,
      declarationId,
      possibleDataKeys,
      foundDataKey,
      hasData: !!data,
      data
    });
    
    return data || null;
  };

  // 🚀 FONCTION CORRIGÉE : TÉLÉCHARGER PDF DÉCLARATION DE MONTAGE
  const downloadDeclarationMontagePDF = async (product, productIndex = 0) => {
    try {
      // 🔥 UTILISER LA MÊME LOGIQUE que getDeclarationMontageStatus pour trouver la bonne clé
      const declarationId = generateDeclarationId(product.name, product.productId, productIndex);
      if (!declarationId) {
        throw new Error("Impossible de générer l'ID de déclaration");
      }
      
      // 🔥 CORRECTION : Trouver l'ID réel utilisé (avec ou sans _unit_1)
      const possibleIds = [
        `${declarationId}_unit_1`,     // Format avec _unit_1
        `${declarationId}`,            // Format sans _unit
        `${product.productId}_unit_1`, // Fallback avec productId
        `${product.productId}`         // Fallback simple
      ];
      
      let actualId = declarationId; // Par défaut
      
      // Trouver lequel existe vraiment dans la base
      for (const id of possibleIds) {
        const completionKey = `${id}_montage_complete`;
        if (order[completionKey] === true) {
          actualId = id;
          console.log(`✅ ID trouvé pour PDF: ${actualId}`);
          break;
        }
      }
      
      const token = localStorage.getItem('authToken');
      const baseURL = process.env.REACT_APP_API_URL || 'https://veryfit.onrender.com';
      
      console.log(`🔍 Téléchargement PDF avec ID: ${actualId}`);
      
      const response = await fetch(`${baseURL}/api/dossiers/${orderId}/products/${actualId}/declaration-montage-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/pdf',
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `declaration_montage_${actualId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement PDF déclaration montage:', error);
      alert(`Erreur lors du téléchargement du PDF: ${error.message}`);
    }
  };

  // 🚀 FONCTION UNIFIED : TÉLÉCHARGER PDF FORMULAIRE DE MISE EN SERVICE
  const downloadFormulaireMiseEnServicePDF = async (product, productIndex = 0) => {
    try {
      const declarationId = generateDeclarationId(product.name, product.productId, productIndex);
      if (!declarationId) {
        throw new Error("Impossible de générer l'ID de déclaration");
      }
      
      const token = localStorage.getItem('authToken');
      const baseURL = process.env.REACT_APP_API_URL || 'https://veryfit.onrender.com';
      
      const response = await fetch(`${baseURL}/api/dossiers/${orderId}/products/${declarationId}/formulaire-mise-en-service-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/pdf',
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `formulaire_mise_en_service_${declarationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement PDF formulaire:', error);
      alert(`Erreur lors du téléchargement du PDF: ${error.message}`);
    }
  };

  // 🔥 FONCTION CORRIGÉE : Calcul des stats globales
  const getGlobalDeclarationStats = () => {
    if (!order || !order.produits) return { completed: 0, total: 0 };
    
    const userRole = getUserRole(order);
    const authorizedDocs = getAuthorizedDocuments(userRole);
    
    if (!authorizedDocs.declarationMontage) {
      return { completed: 0, total: 0 };
    }
    
    let completed = 0;
    const total = order.produits.length;
    
    order.produits.forEach((prod, index) => {
      // Pour chaque produit, vérifier s'il peut avoir plusieurs unités
      const quantity = parseInt(prod.quantite) || 1;
      
      for (let i = 0; i < quantity; i++) {
        if (getDeclarationMontageStatus(prod, i)) {
          completed++;
        }
      }
    });
    
    // Recalcule le total en tenant compte des quantités
    const actualTotal = order.produits.reduce((sum, prod) => {
      return sum + (parseInt(prod.quantite) || 1);
    }, 0);
    
    console.log(`📊 Stats globales déclarations:`, {
      completed,
      total: actualTotal,
      products: order.produits.map(p => ({
        name: p.name,
        quantity: p.quantite
      }))
    });
    
    return { completed, total: actualTotal };
  };

  const exportAllPDFs = async () => {
    const pdf = new jsPDF("p", "mm", "a4");

    for (let i = 0; i < formRefs.current.length; i++) {
      const ref = formRefs.current[i];
      if (!ref) continue;

      const canvas = await html2canvas(ref, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;

      if (i !== 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, 0, width, height);
    }

    pdf.save(`Dossier_CE_${order.orderName || order.id}.pdf`);
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return "—";
    
    if (val instanceof Date) {
      return val.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    
    if (val && typeof val === 'object' && val.toDate && typeof val.toDate === 'function') {
      return val.toDate().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    
    if (typeof val === "boolean") return val ? "✅ Oui" : "❌ Non";
    
    if (Array.isArray(val)) {
      if (val.length === 0) return "—";
      return val.map(item => {
        if (typeof item === 'object' && item !== null) {
          return JSON.stringify(item);
        }
        return String(item);
      }).join(", ");
    }
    
    if (typeof val === 'object' && val !== null) {
      if (val.Collage !== undefined) {
        return String(val.Collage);
      }
      try {
        return JSON.stringify(val, null, 2);
      } catch (e) {
        return "[Objet complexe]";
      }
    }
    
    if (typeof val === "string" && val.trim() === "") return "—";
    
    return String(val);
  };

  const formatDate = (date) => {
    if (!date) return 'Non définie';
    
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return 'Non définie';
    
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'validé':
        return { 
          color: 'bg-emerald-500', 
          icon: '✅', 
          text: 'Validé',
          bgLight: 'bg-emerald-50',
          textColor: 'text-emerald-800'
        };
      case 'en_attente_remplissage':
        return { 
          color: 'bg-amber-500', 
          icon: '⏳', 
          text: 'En attente',
          bgLight: 'bg-amber-50',
          textColor: 'text-amber-800'
        };
      default:
        return { 
          color: 'bg-slate-500', 
          icon: '⚙️', 
          text: status || 'Indéfini',
          bgLight: 'bg-slate-50',
          textColor: 'text-slate-800'
        };
    }
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <VeryfitLoader />
          <p className="text-slate-600 mt-4">Chargement du dossier...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Dossier introuvable</h2>
          <p className="text-slate-600">Le dossier demandé n'existe pas ou n'est plus disponible.</p>
          <button 
            onClick={() => navigate('/fit/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const globalStats = getGlobalDeclarationStats();
  const userRole = getUserRole(order);
  const authorizedDocs = getAuthorizedDocuments(userRole);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header moderne */}
      <div className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/fit/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors p-2 hover:bg-slate-100 rounded-lg"
              >
                <span className="text-lg">←</span>
                <span className="font-medium">Retour</span>
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">F</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Dossier CE</h1>
                  <p className="text-sm text-slate-600 font-mono">{order.orderName}</p>
                  <p className="text-xs text-slate-500">Rôle: {userRole}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium shadow-lg ${statusConfig.color}`}>
                <span>{statusConfig.icon}</span>
                {statusConfig.text}
              </div>
              <button 
                onClick={exportAllPDFs}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-md"
              >
                <span>📥</span>
                Télécharger Dossier Complet
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar avec informations du dossier */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">Informations</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-slate-400 mt-1">#</span>
                  <div className="flex-1 min-w-0">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Numéro</label>
                    <p className="text-sm text-slate-900 font-mono bg-slate-50 px-2 py-1 rounded mt-1 break-all">
                      {order.id}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-slate-400 mt-1">👤</span>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Destinataire</label>
                    <p className="text-sm text-slate-900 mt-1">
                      {(() => {
                        const entreprise = order.entreprise || 
                                          order.nomEntreprise || 
                                          order.clientName || 
                                          order.orderName || 
                                          order.revendeur || 
                                          "—";
                        
                        const contact = order.contactName || order.nomContact || "";
                        
                        if (contact && entreprise !== contact) {
                          return `${entreprise} (${contact})`;
                        }
                        
                        return entreprise;
                      })()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-slate-400 mt-1">🏷️</span>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Type</label>
                    <p className="text-sm text-slate-900 mt-1 font-medium">{order.destinataire_type || "—"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-slate-400 mt-1">📧</span>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Email</label>
                    <p className="text-sm text-slate-900 mt-1 break-all">{order.revendeurEmail || "—"}</p>
                  </div>
                </div>
                
                {order.createdAt && (
                  <div className="flex items-start gap-3">
                    <span className="text-slate-400 mt-1">📅</span>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Créé le</label>
                      <p className="text-sm text-slate-900 mt-1">{formatDate(order.createdAt)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 🔥 SECTION DÉCLARATIONS DE MONTAGE - SEULEMENT POUR CARROSSIER */}
              {authorizedDocs.declarationMontage && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Déclarations de montage</h3>
                    <div className="flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded-full">
                      <span className="font-medium">{globalStats.completed}</span>
                      <span className="text-slate-500">/{globalStats.total}</span>
                    </div>
                  </div>
                  
                  {order.produits && order.produits.length > 0 ? (
                    <div className="space-y-2">
                      {order.produits.map((prod, index) => {
                        const quantity = parseInt(prod.quantite) || 1;
                        
                        // Créer une entrée pour chaque unité du produit
                        const units = Array.from({ length: quantity }, (_, unitIndex) => {
                          const isCompleted = getDeclarationMontageStatus(prod, unitIndex);
                          const displayName = quantity > 1 ? `${prod.name} (${unitIndex + 1}/${quantity})` : prod.name;
                          
                          return (
                            <div key={`${index}-${unitIndex}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isCompleted ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                <span className="text-xs text-slate-900 truncate" title={displayName}>
                                  {displayName}
                                </span>
                              </div>
                              {isCompleted ? (
                                <button
                                  onClick={() => downloadDeclarationMontagePDF(prod, unitIndex)}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                  title="Télécharger la déclaration"
                                >
                                  📄 PDF
                                </button>
                              ) : (
                                <span className="text-xs text-red-600 px-2 py-1">❌</span>
                              )}
                            </div>
                          );
                        });
                        
                        return units;
                      }).flat()}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Aucun produit</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="lg:col-span-3">
            {/* Overview des produits */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <span className="text-2xl">📦</span>
                  Produits ({order.produits?.length || 0})
                </h2>
                {authorizedDocs.declarationMontage && globalStats.total > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600">Déclarations:</span>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      globalStats.completed === globalStats.total 
                        ? 'bg-emerald-100 text-emerald-800' 
                        : globalStats.completed > 0
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {globalStats.completed}/{globalStats.total} reçues
                    </div>
                  </div>
                )}
              </div>

              {!order.produits || order.produits.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="font-medium text-slate-900 mb-2">Aucun produit</h3>
                  <p className="text-slate-600">Aucun produit associé à ce dossier</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {order.produits.map((prod, index) => {
                    const quantity = parseInt(prod.quantite) || 1;
                    let completedUnits = 0;
                    
                    // Compter les unités complétées pour ce produit
                    if (authorizedDocs.declarationMontage) {
                      for (let i = 0; i < quantity; i++) {
                        if (getDeclarationMontageStatus(prod, i)) {
                          completedUnits++;
                        }
                      }
                    }
                    
                    return (
                      <div
                        key={index}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                          selectedProduct === index 
                            ? 'border-blue-500 bg-blue-50 shadow-lg' 
                            : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                        }`}
                        onClick={() => setSelectedProduct(index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${prod.filled ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{prod.name}</h3>
                              <p className="text-sm text-slate-600">
                                Type: {prod.typeFormulaire} • Série: {prod.porte?.NumeroSerie || "—"}
                                {quantity > 1 && ` • Quantité: ${quantity}`}
                              </p>
                              {authorizedDocs.declarationMontage && quantity > 1 && (
                                <p className="text-xs text-blue-600 mt-1">
                                  📋 {completedUnits}/{quantity} déclarations montage reçues
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {/* Statut formulaire CE - SELON LE RÔLE */}
                            {(authorizedDocs.declarationCE || authorizedDocs.miseEnService) && (
                              prod.filled ? (
                                <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium px-3 py-1 bg-emerald-50 rounded-full">
                                  <span>✅</span>
                                  {authorizedDocs.miseEnService ? "Mise en service" : "CE Rempli"}
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-600 text-sm font-medium px-3 py-1 bg-red-50 rounded-full">
                                  <span>❌</span>
                                  {authorizedDocs.miseEnService ? "En attente mise en service" : "CE En attente"}
                                </div>
                              )
                            )}
                            
                            {/* Statut déclaration montage - SEULEMENT POUR CARROSSIER */}
                            {authorizedDocs.declarationMontage && (
                              completedUnits === quantity ? (
                                <div className="flex items-center gap-1 text-blue-600 text-sm font-medium px-3 py-1 bg-blue-50 rounded-full">
                                  <span>📋</span>
                                  {quantity > 1 ? `${completedUnits}/${quantity} Montages` : "Montage Reçu"}
                                </div>
                              ) : completedUnits > 0 ? (
                                <div className="flex items-center gap-1 text-amber-600 text-sm font-medium px-3 py-1 bg-amber-50 rounded-full">
                                  <span>📋</span>
                                  {completedUnits}/{quantity} Montages
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-orange-600 text-sm font-medium px-3 py-1 bg-orange-50 rounded-full">
                                  <span>⏳</span>
                                  Montage En attente
                                </div>
                              )
                            )}

                            {/* Statut contrôle périodique - POUR REVENDEUR ET UTILISATEUR */}
                            {authorizedDocs.controlePeriodique && (
                              <div className="flex items-center gap-1 text-amber-600 text-sm font-medium px-3 py-1 bg-amber-50 rounded-full">
                                <span>🔍</span>
                                Contrôle à venir
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Détails du produit sélectionné */}
            {order.produits && order.produits[selectedProduct] && (
              <div 
                ref={(el) => (formRefs.current[selectedProduct] = el)}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-slate-900">
                    {order.produits[selectedProduct].name}
                  </h2>
                </div>

                {/* 🔥 SECTION DÉCLARATION DE MONTAGE - SEULEMENT POUR CARROSSIER */}
                {authorizedDocs.declarationMontage && (() => {
                  const currentProduct = order.produits[selectedProduct];
                  const quantity = parseInt(currentProduct.quantite) || 1;
                  
                  return (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <span className="text-xl">🔧</span>
                        Déclaration de montage carrossier
                        {quantity > 1 && (
                          <span className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                            {quantity} unités
                          </span>
                        )}
                      </h3>
                      
                      {quantity === 1 ? (
                        // Affichage pour une seule unité
                        (() => {
                          const declarationCompleted = getDeclarationMontageStatus(currentProduct, 0);
                          const declarationData = getDeclarationMontageData(currentProduct, 0);
                          
                          return declarationCompleted && declarationData ? (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-2 text-emerald-800 font-medium">
                                  <span className="text-xl">✅</span>
                                  Déclaration de montage reçue
                                </div>
                                <button
                                  onClick={() => downloadDeclarationMontagePDF(currentProduct, 0)}
                                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                  <span>📄</span>
                                  Télécharger PDF
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white rounded-lg p-4">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Nom du carrossier
                                  </label>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {declarationData.nomCarrossier || "—"}
                                  </p>
                                </div>
                                
                                <div className="bg-white rounded-lg p-4">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Date de montage
                                  </label>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {declarationData.dateMontage || "—"}
                                  </p>
                                </div>
                                
                                <div className="bg-white rounded-lg p-4">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Numéro de série
                                  </label>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {declarationData.numeroSerie || "—"}
                                  </p>
                                </div>
                                
                                <div className="bg-white rounded-lg p-4 md:col-span-2 lg:col-span-3">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Observations
                                  </label>
                                  <p className="text-sm text-slate-900">
                                    {declarationData.observations || "Aucune observation"}
                                  </p>
                                </div>
                                
                                <div className="bg-white rounded-lg p-4 md:col-span-2 lg:col-span-3">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Date de soumission
                                  </label>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {formatDate(declarationData.submittedAt)}
                                  </p>
                                </div>
                              </div>
                              
                              {declarationData.signature && (
                                <div className="mt-4 pt-4 border-t border-emerald-200">
                                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-2">
                                    Signature carrossier
                                  </label>
                                  <div className="bg-white rounded-lg p-4 inline-block">
                                    <img 
                                      src={declarationData.signature} 
                                      alt="Signature carrossier" 
                                      className="max-w-xs max-h-24 object-contain"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                              <div className="text-4xl mb-4">⏳</div>
                              <h4 className="font-semibold text-red-800 mb-2">Déclaration de montage non reçue</h4>
                              <p className="text-red-600 text-sm">
                                En attente de la déclaration de montage du carrossier pour ce produit.
                              </p>
                            </div>
                          );
                        })()
                      ) : (
                        // Affichage pour plusieurs unités
                        <div className="space-y-4">
                          {Array.from({ length: quantity }, (_, unitIndex) => {
                            const declarationCompleted = getDeclarationMontageStatus(currentProduct, unitIndex);
                            const declarationData = getDeclarationMontageData(currentProduct, unitIndex);
                            
                            return (
                              <div key={unitIndex} className="border border-slate-200 rounded-xl overflow-hidden">
                                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                                  <h4 className="font-medium text-slate-900 flex items-center gap-2">
                                    <span className="text-base">📦</span>
                                    Unité {unitIndex + 1}/{quantity}
                                    {declarationCompleted ? (
                                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                                        ✅ Reçue
                                      </span>
                                    ) : (
                                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                        ⏳ En attente
                                      </span>
                                    )}
                                  </h4>
                                </div>
                                
                                {declarationCompleted && declarationData ? (
                                  <div className="p-4 bg-emerald-50">
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="text-sm text-emerald-800 font-medium">
                                        Déclaration reçue le {formatDate(declarationData.submittedAt)}
                                      </span>
                                      <button
                                        onClick={() => downloadDeclarationMontagePDF(currentProduct, unitIndex)}
                                        className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded-lg font-medium transition-colors"
                                      >
                                        📄 PDF
                                      </button>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      <div className="bg-white rounded p-3">
                                        <label className="text-xs text-slate-600 block mb-1">Carrossier</label>
                                        <p className="text-sm font-medium">{declarationData.nomCarrossier || "—"}</p>
                                      </div>
                                      <div className="bg-white rounded p-3">
                                        <label className="text-xs text-slate-600 block mb-1">Date montage</label>
                                        <p className="text-sm font-medium">{declarationData.dateMontage || "—"}</p>
                                      </div>
                                      <div className="bg-white rounded p-3">
                                        <label className="text-xs text-slate-600 block mb-1">N° série</label>
                                        <p className="text-sm font-medium">{declarationData.numeroSerie || "—"}</p>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 bg-red-50 text-center">
                                    <p className="text-sm text-red-600">
                                      Déclaration de montage non reçue pour cette unité
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Section CE Formulaire / Mise en service */}
                {(authorizedDocs.declarationCE || authorizedDocs.miseEnService) && (
                  order.produits[selectedProduct].filled ? (
                    <div className="space-y-6">
                      {/* Statut rempli avec bouton téléchargement */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-emerald-800 font-medium">
                            <span className="text-xl">✅</span>
                            {authorizedDocs.miseEnService ? "Formulaire de mise en service rempli" : "Formulaire CE rempli"}
                          </div>
                          
                          {/* 🚀 BOUTON UNIQUE : Télécharger PDF Formulaire */}
                          <button
                            onClick={() => downloadFormulaireMiseEnServicePDF(order.produits[selectedProduct], 0)}
                            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                          >
                            <span>📋</span>
                            Télécharger PDF
                          </button>
                        </div>
                      </div>

                      {/* Données du formulaire - Aperçu condensé */}
                      {(() => {
                        const data = order.produits[selectedProduct].formulaire || order.produits[selectedProduct].formulaireData || {};
                        if (Object.keys(data).length === 0) {
                          return (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                              <div className="flex items-center gap-2 text-amber-800 font-medium">
                                <span className="text-xl">⚠️</span>
                                Formulaire marqué comme rempli mais aucune donnée disponible
                              </div>
                            </div>
                          );
                        }

                        // Aperçu condensé des données principales
                        const mainSections = Object.entries(data).slice(0, 3); // Afficher seulement les 3 premières sections
                        
                        return (
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900 text-sm">Aperçu des données :</h4>
                            {mainSections.map(([sectionName, sectionContent], i) => (
                              <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                                  <h5 className="font-medium text-slate-900 text-sm flex items-center gap-2">
                                    <span className="text-base">⚙️</span>
                                    {sectionName}
                                  </h5>
                                </div>
                                <div className="p-4">
                                  {sectionContent && typeof sectionContent === 'object' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {Object.entries(sectionContent).slice(0, 4).map(([field, value], j) => (
                                        <div key={j} className="bg-slate-50 rounded p-2">
                                          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide block mb-1">
                                            {field}
                                          </label>
                                          <p className="text-sm text-slate-900">
                                            {formatValue(value)}
                                          </p>
                                        </div>
                                      ))}
                                      {Object.keys(sectionContent).length > 4 && (
                                        <div className="text-xs text-slate-500 italic col-span-full">
                                          ... et {Object.keys(sectionContent).length - 4} autre(s) champ(s)
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-slate-600 text-sm">{formatValue(sectionContent)}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            {Object.keys(data).length > 3 && (
                              <div className="text-center">
                                <p className="text-sm text-slate-500 italic">
                                  ... et {Object.keys(data).length - 3} autre(s) section(s) disponibles dans le PDF complet
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Documents associés - Version épurée */}
                      <div className="border border-slate-200 rounded-xl p-6">
                        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <span className="text-lg">📄</span>
                          Documents associés
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Déclaration CE - SELON LE RÔLE */}
                          {authorizedDocs.declarationCE && (
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <span className="text-blue-600 text-lg">📄</span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">Déclaration CE</p>
                                  <p className="text-sm text-slate-600">Document officiel</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-emerald-600 text-sm px-3 py-1 bg-emerald-50 rounded-lg">
                                <span>✅</span>
                                Disponible
                              </div>
                            </div>
                          )}
                          
                          {/* Notice d'instruction - POUR TOUS */}
                          {authorizedDocs.noticeInstruction && (
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                  <span className="text-purple-600 text-lg">📖</span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">Notice d'instruction</p>
                                  <p className="text-sm text-slate-600">Guide d'installation</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-emerald-600 text-sm px-3 py-1 bg-emerald-50 rounded-lg">
                                <span>✅</span>
                                Disponible
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                      <div className="text-6xl mb-4">❌</div>
                      <h3 className="font-semibold text-red-800 mb-2">
                        {authorizedDocs.miseEnService ? "Mise en service non effectuée" : "Formulaire non rempli"}
                      </h3>
                      <p className="text-red-600">
                        {authorizedDocs.miseEnService 
                          ? "Ce produit est en attente de mise en service." 
                          : "Ce formulaire est en attente de completion."
                        }
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitOrderDetails;